import { PropsWithChildren } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.scss';
import splashScreens from './splash-screens.json';
import FirebaseProvider from './components/FirebaseProvider';
import AuthProvider from './components/AuthProvider';
import GlobalError from './components/GlobalError';
import LoadingSpinner from './components/LoadingSpinner';

const queryClient = new QueryClient();
const splashScreenLinks = splashScreens.flatMap(({ width, height, ratio }) =>
  (['portrait', 'landscape'] as const).map(orientation => {
    const [pixelWidth, pixelHeight] =
      orientation === 'portrait' ? [width * ratio, height * ratio] : [height * ratio, width * ratio];

    return (
      <link
        key={`${width}x${height}@${ratio}-${orientation}`}
        rel="apple-touch-startup-image"
        href={`/splash/apple-splash-${pixelWidth}x${pixelHeight}.png`}
        media={`(device-width: ${width}px) and (device-height: ${height}px) and (-webkit-device-pixel-ratio: ${ratio}) and (orientation: ${orientation})`}
      />
    );
  })
);

export function Layout({ children }: PropsWithChildren) {
  return (
    <html lang="de">
      <head>
        <meta charSet="utf-8" />
        <link rel="icon" href="/favicon.ico" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        <meta name="theme-color" content="#212529" />
        <meta name="description" content="Shopping List App" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" sizes="180x180" />
        {splashScreenLinks}
        {/* vite-plugin-pwa can no longer inject this — there is no index.html to transform. */}
        <link rel="manifest" href="/manifest.webmanifest" />
        <title>Shopping List</title>
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// This, not the default export, is what gets prerendered into index.html at build time
// (ssr: false). Keeping it separate means Firebase never initializes during the build.
export function HydrateFallback() {
  return <LoadingSpinner />;
}

export default function Root() {
  return (
    // react-error-boundary stays: AuthProvider, useDoc and useCollectionWithFilter all report
    // onSnapshot failures imperatively through useErrorBoundary().showBoundary, which the
    // router's own ErrorBoundary export does not provide.
    <ReactErrorBoundary FallbackComponent={GlobalError}>
      <QueryClientProvider client={queryClient}>
        <FirebaseProvider>
          <AuthProvider>
            <Outlet />
          </AuthProvider>
        </FirebaseProvider>
      </QueryClientProvider>
    </ReactErrorBoundary>
  );
}
